/**
 * Formats a number as a price string.
 *
 * @param {number} number - The number to format as a price.
 * @param {Object} [options] - Configuration options for formatting.
 * @param {string} [options.symbol="$"] - The currency symbol to use.
 * @param {number} [options.decimal_place=2] - The number of decimal places to display.
 * @param {string} [options.decimal_separator="."] - The character to use as the decimal separator.
 * @param {string} [options.symbol_location="left"] - The position of the currency symbol ("left" or "right").
 * @param {string} [options.thousands_separator=","] - The character to use as the thousands separator.
 * @returns {string} The formatted price string.
 *
 * @example
 * formatPrice(1234.56);
 * // Returns "$1,234.56"
 *
 * @example
 * formatPrice(1234.56, { symbol: "€", symbol_location: "right" });
 * // Returns "1,234.56€"
 *
 * @example
 * formatPrice(5, null);
 * // Returns "$5.00"
 */

export const formatPrice = (number, options = {}) => {
    const {
        symbol = '$',
        decimal_place = 2,
        decimal_separator = '.',
        symbol_location = 'left',
        thousands_separator = ',',
    } = options || {}; // Ensures default values are used if options is null

    const roundedNumber = roundPrice(number, decimal_place); // Use the new roundPrice function.
    let [integerPart, decimalPart] = roundedNumber
        .toFixed(decimal_place)
        .split('.');

    integerPart = integerPart.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        thousands_separator
    );
    let formattedNumber = integerPart;

    if (decimalPart) {
        formattedNumber += decimal_separator + decimalPart;
    }

    return symbol_location.toLowerCase() === 'left'
        ? symbol + formattedNumber
        : formattedNumber + symbol;
};

export const roundPrice = (number, decimalPlaces = 2) => {
    const factor = Math.pow(10, decimalPlaces);
    return Math.round(number * factor) / factor;
};
